import React, { useState, useEffect } from 'react';
import { Search, Plus, Download, Trash2, Mail, MessageSquare, Calendar, TrendingUp, Send, Eye, Users, BarChart3, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from 'react-router-dom';

const Campaigns = () => {
  const { user } = useAuth();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [campaigns, setCampaigns] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('campaigns')
          .eq('id', user?.id)
          .single();

        if (error) {
          console.error('Error fetching campaigns:', error);
          return;
        }

        const campaignsArray = data?.campaigns?.map((c, idx) => {
          try {
            const parsed = JSON.parse(c);
            // ensure an id exists so we can route; fallback to index string
            const withId = parsed?.id ? parsed : { ...parsed, id: parsed.id ?? String(idx) };
            // keep track of the raw index in the profiles.campaigns text[] array for robust deletes
            return { ...withId, _rawIndex: idx };
          } catch (e) {
            return null;
          }
        }).filter(Boolean) || [];
        setCampaigns(campaignsArray);
      } catch (error) {
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchCampaigns();
    }
  }, [user?.id]);

  const filteredCampaigns = campaigns.filter(campaign => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      (campaign.subject && campaign.subject.toLowerCase().includes(term)) ||
      (campaign.message && campaign.message.toLowerCase().includes(term)) ||
      (campaign.type && campaign.type.toLowerCase().includes(term));
    const matchesFilter = filterType === 'All' || campaign.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      // Load current campaigns text[] from profiles
      const { data: prof, error: fetchErr } = await supabase
        .from('profiles')
        .select('campaigns')
        .eq('id', user?.id)
        .single();
      if (fetchErr) {
        console.error('Error fetching campaigns for delete:', fetchErr);
        toast.error('Failed to delete campaign. Please try again.');
        return;
      }

      const arr = Array.isArray(prof?.campaigns) ? prof.campaigns.slice() : [];
      // Find which entry to remove: prefer id match; fallback to stored _rawIndex; final fallback: match on essentials
      const findIndexById = () => {
        if (!campaignToDelete?.id) return -1;
        for (let i = 0; i < arr.length; i++) {
          try {
            const obj = JSON.parse(arr[i]);
            if (obj && String(obj.id) === String(campaignToDelete.id)) return i;
          } catch {}
        }
        return -1;
      };
      let indexToRemove = findIndexById();
      if (indexToRemove === -1 && typeof campaignToDelete?._rawIndex === 'number') {
        if (campaignToDelete._rawIndex >= 0 && campaignToDelete._rawIndex < arr.length) {
          indexToRemove = campaignToDelete._rawIndex;
        }
      }
      if (indexToRemove === -1) {
        // Fallback heuristic: match by type+subject+message
        const { type, subject, message } = campaignToDelete || {};
        for (let i = 0; i < arr.length; i++) {
          try {
            const obj = JSON.parse(arr[i]);
            if ((obj?.type || null) === (type || null) && (obj?.subject || null) === (subject || null) && (obj?.message || null) === (message || null)) {
              indexToRemove = i; break;
            }
          } catch {}
        }
      }

      if (indexToRemove === -1) {
        toast.error('Could not locate this campaign in your account. Please refresh and try again.');
        return;
      }

      const serverUpdated = arr.filter((_, i) => i !== indexToRemove);
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({ campaigns: serverUpdated })
        .eq('id', user?.id);
      if (updateErr) {
        console.error('Error updating campaigns array:', updateErr);
        toast.error('Failed to delete campaign. Please try again.');
        return;
      }

      // Update local state: prefer id match; fallback to _rawIndex or essentials
      setCampaigns((prev) => {
        if (campaignToDelete?.id) return prev.filter(c => String(c.id) !== String(campaignToDelete.id));
        if (typeof campaignToDelete?._rawIndex === 'number') return prev.filter((_, i) => i !== campaignToDelete._rawIndex);
        const { type, subject, message } = campaignToDelete || {};
        return prev.filter(c => !(c.type === type && c.subject === subject && c.message === message));
      });

      toast.success('Campaign deleted successfully!');
      setIsDeleteDialogOpen(false);
      setCampaignToDelete(null);
      if (selectedCampaign?.id === campaignToDelete.id) {
        setSelectedCampaign(null);
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const exportCampaignsToCSV = () => {
    const csvContent = [
      ['Type', 'Subject', 'Message', 'Date'].join(','),
      ...campaigns.map(campaign => [
        campaign.type,
        campaign.subject || 'N/A',
        campaign.message || 'N/A',
        campaign.createdAt || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'campaigns.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Campaign list exported successfully!');
  };

  const getCampaignTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'sms':
        return <MessageSquare className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'social':
        return <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
      default:
        return <Send className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getCampaignTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700';
      case 'sms':
        return 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700';
      case 'social':
        return 'bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-700';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200 dark:bg-gray-700/20 dark:text-gray-400 dark:border-gray-600';
    }
  };

  // Calculate stats
  const totalCampaigns = campaigns.length;
  const emailCampaigns = campaigns.filter(c => c.type?.toLowerCase() === 'email').length;
  const smsCampaigns = campaigns.filter(c => c.type?.toLowerCase() === 'sms').length;
  const socialCampaigns = campaigns.filter(c => c.type?.toLowerCase() === 'social').length;

  // Get unique campaign types for filter
  const campaignTypes = ['All', ...new Set(campaigns.map(c => c.type).filter(Boolean))];

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Campaign Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track and analyze your marketing campaigns
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCampaignsToCSV}
             className="px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20
 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <a href="/create-campaign" className="hidden sm:inline">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <Plus className="w-4 h-4" />
                New Campaign
              </button>
            </a>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
       <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalCampaigns}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Campaigns
              </p>
            </div>
          </div>

<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {emailCampaigns}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email Campaigns
              </p>
            </div>
          </div>

<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-50 dark:bg-green-500/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {smsCampaigns}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                SMS Campaigns
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search campaigns by subject, message, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-transparent text-gray-900 dark:text-white"
              >
                {campaignTypes.map(type => (
                  <option key={type} value={type}>{type.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Campaign List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Campaigns ({filteredCampaigns.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && (
                  <div className="p-6 text-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Loading campaigns...</p>
                  </div>
                )}

                {!loading && filteredCampaigns.length === 0 && (
                  <div className="p-6 text-center">
                    <Send className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No campaigns found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Create your first campaign to get started</p>
                  </div>
                )}

                {!loading && filteredCampaigns.map((campaign, index) => (
                  <div
                    key={campaign.id || index}
                    className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      selectedCampaign?.id === campaign.id ? "bg-white dark:bg-transparent border-r-4 border-blue-500" : ""
                    }`}
                    onClick={() => {
                      const id = campaign.id ?? String(index);
                      navigate(`/campaigns/${encodeURIComponent(id)}`);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 min-w-0 flex-1">
                        <div className="w-12 h-12 bg-transparent rounded-lg flex items-center justify-center text-white">
                          {getCampaignTypeIcon(campaign.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {campaign.subject || 'SMS Campaign'}
                            </h3>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCampaignTypeColor(campaign.type)}`}>
                              {campaign.type?.toUpperCase() || 'CAMPAIGN'}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                            {campaign.message || 'No message content'}
                          </p>
                        </div>
                      </div>
                      {campaign.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCampaignToDelete(campaign);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column previously showed details; now empty to keep layout balanced on large screens */}
          <div className="hidden lg:block" />
        </div>

        {/* Delete Confirmation Modal */}
        {isDeleteDialogOpen && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-black rounded-xl shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Delete Campaign</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete this campaign? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setCampaignToDelete(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteCampaign}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;